// Promoted from the SmallBiz Design Studio (tools/design-studio/variants/night-flight/index.tsx) by pnpm studio:promote (EPIC #7375).
// This copy is now www-sbi code: edit it here. The studio original stays for comparison.
/**
 * Night Flight (Winter, 2026-10-13; round 2 2026-10-14 after Ben's huddle notes).
 *
 * Page: one night sky stays fixed behind the whole page in two parallax layers. Text-heavy
 * sections are solid rounded cards (no stars behind text); light sections float on the sky.
 * Header: one floating bar that splits into two globs (logo left, actions right) as you scroll.
 * Hero: a night sky. As you scroll, a paper plane draws one smooth contrail, stars land on it
 * just ahead of the plane, and the three hero words come into focus one at a time (blurred to
 * sharp). Near the top the plane folds into a small rocket (idea to launch, Ben's huddle
 * 2026-10-13). It ends on "We fly it with you." and the consult button.
 * Positioning: "We work inside your business, not for it." replaces the freelancers / agencies
 * / coaches comparison. Reasons: the current cards, last line without the freelancer comparison.
 * About: centred and calmer: three promises, a philosophy marquee, the founders in airplane windows.
 * Growth: the seven service areas circle in a slow orbit that turns on its own; the wheel over
 * it, or a click, turns it. The area in front is the one you read, with its link.
 * Cta + Footer: the closing call to action moves into the footer landing, on the same sky (no
 * loud colour band); a slow golden glow sits behind the main button. Then the current footer's
 * cards, link columns and legal row, restyled.
 *
 * Inspiration (patterns only, no layout, art or copy taken): a scroll-assembled star field,
 * focus-in text beats, a clean dark base with small motion, a scroll-turned card orbit.
 *
 * Canon check (sbi-agent-beacon):
 * - Copy: the hero triad and spine (VOICE_RULES.md 42-43). One flight motif line on the
 *   surface: "We fly it with you." The star path is a device, not copy.
 * - Demetrius: none on this page. No Wingtip device (the star path is not the Wingtip).
 * - Grounds: dark mode is the brand-dark night scene. No purple, violet or indigo.
 *   Light mode (Winter, 2026-10-15) is a 2D doodle: paper with a dot grid, black outlines, flat
 *   fills, a dashed pencil contrail, outlined sparkles and hard offset shadows. No glows.
 * - Motion: ease in and out, calm. Reduced motion shows the finished sky and a plain list.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { HeaderSearch } from './HeaderSearch';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotionConfig } from 'motion/react';
import { ArrowRight, ChevronDown, Menu, Target, TrendingUp, Users, X } from 'lucide-react';
import { SBILogo } from '../OptimizedImage';
import { ThemeSwitcher } from '../ThemeSwitcher';
import { getActiveServices } from '../../../data/services';
import { LiveStrip } from './LiveStrip';
import { ctaAttrs, getStartedCtaLabel } from '../../../lib/analytics';

export const meta = {
  title: 'Night Flight',
  concept: 'Night Flight',
  status: 'pick',
  notes:
    'One night sky behind the whole page, text sections as solid cards. The header splits into two globs as you scroll. In the hero the paper plane draws its contrail and becomes a rocket near the top. New positioning line, no comparisons with freelancers, agencies or coaches. Calmer About with the founders in airplane windows. Growth orbit and a night-sky footer landing.',
};

const BLUE = '#4A89F7';

/**
 * The two modes. Dark is the night scene. Light (2026-10-15) is the doodle: paper, black ink
 * outlines, flat fills and hard offset shadows. The studio sets `.dark` on <html>. Each section
 * that reads these renders this block, so a Mix that takes one section alone still gets them.
 */
const INK = '#111111';
const PAPER_DOTS = 'radial-gradient(rgba(17,17,17,.13) 1.2px, transparent 1.4px) 0 0 / 24px 24px';
const NF_THEME = `
html {
  --nf-page: #FFFBF2; --nf-glow: #FFFBF2; --nf-paper-dots: ${PAPER_DOTS};
  --nf-star: #111111; --nf-star-alpha: 0;
  --nf-ink: #111111; --nf-ink-soft: rgba(17,17,17,.78); --nf-ink-faint: rgba(17,17,17,.62);
  --nf-line: #111111; --nf-glass: #FFFFFF; --nf-glass-hover: #FFF1C2;
  --nf-accent: #1F5FD1; --nf-accent-hover: #17489F;
  --nf-pill: #F5C542; --nf-gold: var(--sunshine-800);
  --nf-card-shadow: 6px 6px 0 #111111;
  --nf-foot: #FFFBF2;
}
html.dark {
  --nf-paper-dots: none;
  --nf-page: #050B1F; --nf-glow: #0E2350;
  --nf-star: #FFFFFF; --nf-star-alpha: 1;
  --nf-ink: #FFFFFF; --nf-ink-soft: rgba(255,255,255,.75); --nf-ink-faint: rgba(255,255,255,.6);
  --nf-line: rgba(255,255,255,.15); --nf-glass: rgba(255,255,255,.05); --nf-glass-hover: rgba(255,255,255,.1);
  --nf-accent: #6FA3FF; --nf-accent-hover: #9CC2FF;
  --nf-pill: #0E2350; --nf-gold: var(--sunshine);
  --nf-card-shadow: 0 24px 60px -30px rgba(0,0,0,.6);
  --nf-foot: linear-gradient(180deg, #050B1F 0%, #071330 55%, #050B1F 100%);
  --nf-trail-end: #CFE0FF; --nf-plane-1: #FFFFFF; --nf-plane-2: #C9D8F5; --nf-plane-3: #8FAEE6;
  --nf-plane-glow: #9CC2FF; --nf-plane-edge: transparent;
}
.nf-ink { color: var(--nf-ink); }
.nf-ink-soft { color: var(--nf-ink-soft); }
.nf-ink-faint { color: var(--nf-ink-faint); }
.nf-accent { color: var(--nf-accent); }
.nf-gold { color: var(--nf-gold); }
a.nf-hover-accent:hover { color: var(--nf-accent-hover); }
a.nf-hover-gold:hover, .group:hover .nf-group-gold { color: var(--nf-gold); }
.nf-card { background: var(--nf-glass); border-color: var(--nf-line); }
.nf-ghost { background: var(--nf-glass); border-color: var(--nf-line); }
.nf-ghost:hover { background: var(--nf-glass-hover); }
.nf-lead { background: rgba(74,137,247,.15); border-color: rgba(74,137,247,.4); }
html:not(.dark) .nf-lead { background: #DCEAFF; border-color: #111; }
.nf-dot { box-shadow: 0 0 14px 4px rgba(74,137,247,.45); }
.nf-window { background: linear-gradient(160deg, #2A3D66 0%, #142446 55%, #0B1733 100%); box-shadow: 0 18px 40px -18px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.12); }
/* The doodle: black ink outlines and hard offset shadows, no glows. */
html:not(.dark) .nf-glow-only { display: none; }
html:not(.dark) .nf-card, html:not(.dark) .nf-ghost { border-width: 2.5px; box-shadow: 4px 4px 0 #111; }
html:not(.dark) .nf-dot { box-shadow: none; border: 2.5px solid #111; background: #F5C542; }
html:not(.dark) .nf-window { background: #FFFFFF; border: 2.5px solid #111; box-shadow: 5px 5px 0 #111; }
html:not(.dark) .nf-window-pane { box-shadow: inset 0 0 0 2.5px #111 !important; }
html:not(.dark) .card-primary-cta { border: 2.5px solid #111; box-shadow: 4px 4px 0 #111 !important; }
html:not(.dark) .nf-pill { border-width: 2px; }
html:not(.dark) .nf-pill-on { box-shadow: 3px 3px 0 #111; border-color: #111 !important; }
html:not(.dark) .nf-orbit { border-width: 2px; border-style: dashed; }
/* The doodle menu bar: white panels, black ink, a yellow marker on hover. Set here, not read
   from the brand theme, for the same reason as the cards. */
html:not(.dark) .nf-header {
  --foreground: #111111; --border: #111111; --primary: #1F5FD1; --card: #FFFFFF; --background: #FFFFFF;
  --color-foreground: #111111; --color-border: #111111; --color-primary: #1F5FD1;
  --color-background: #FFFFFF; --color-card: #FFFFFF; color: #111111;
}
html:not(.dark) .nf-header .glass-morphism {
  background: #FFFFFF !important; backdrop-filter: none !important; -webkit-backdrop-filter: none !important;
  border: 2.5px solid #111111 !important; box-shadow: 4px 4px 0 #111111 !important;
}
html:not(.dark) .nf-header .glass-morphism a:hover { background: #F5C542 !important; color: #111111 !important; }
html:not(.dark) .nf-header button:hover { color: #1F5FD1; }
html:not(.dark) .nf-header button.rounded-full { background: #FFFFFF !important; color: #111111 !important; border: 2px solid #111111 !important; box-shadow: 2px 2px 0 #111111 !important; }
`;

/** True while the studio (or the site) is in dark mode. Follows the `.dark` class on <html>. */
function useDark() {
  const [dark, setDark] = useState(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const html = document.documentElement;
    const read = () => setDark(html.classList.contains('dark'));
    const mo = new MutationObserver(read);
    mo.observe(html, { attributes: true, attributeFilter: ['class'] });
    read();
    return () => mo.disconnect();
  }, []);
  return dark;
}

/** The doodle outline: black ink, round joins, the same weight at any scale. */
const OUT = { stroke: INK, strokeWidth: 2.4, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const, vectorEffect: 'non-scaling-stroke' as const };

/** A four-point sparkle, drawn with curved (pinched) sides. */
const sparkle = (r: number) => `M 0 ${-r} Q 0 0 ${r} 0 Q 0 0 0 ${r} Q 0 0 ${-r} 0 Q 0 0 0 ${-r} Z`;

/** A small cloud, about 80 x 30, its base on y = 20. */
const CLOUD_D = 'M 0 20 Q -6 6 10 4 Q 14 -10 32 -6 Q 44 -18 60 -2 Q 78 -2 74 20 Z';

/** The doodle paper plane. Nose points along +x. */
function DoodlePlane() {
  return (
    <>
      <path d="M 30 0 L -20 -15 L -9 0 Z" fill="#FFFFFF" {...OUT} />
      <path d="M 30 0 L -9 0 L -16 13 Z" fill="#9CC2FF" {...OUT} />
      <path d="M 30 0 L -9 0 L -5 5 Z" fill={BLUE} {...OUT} />
    </>
  );
}

/** The doodle rocket: flame, fins, body, window. */
function DoodleRocket() {
  return (
    <>
      <path d="M -14 -4.5 L -32 0 L -14 4.5 Z" fill="#F5C542" className="nf-flame" {...OUT} />
      <path d="M -8 -6.5 L -19 -15 L -15 -5.5 Z" fill={BLUE} {...OUT} />
      <path d="M -8 6.5 L -19 15 L -15 5.5 Z" fill={BLUE} {...OUT} />
      <path d="M 28 0 C 22 -8.5, 6 -8.5, -15 -7 L -15 7 C 6 8.5, 22 8.5, 28 0 Z" fill="#FFFFFF" {...OUT} />
      <path d="M -15 -7 L -15 7 L -11 7 L -11 -7 Z" fill="#9CC2FF" {...OUT} />
      <circle cx={9} cy={0} r={3.4} fill={BLUE} {...OUT} />
    </>
  );
}

function NfTheme() {
  return <style>{NF_THEME}</style>;
}

const clamp = (v: number) => Math.min(1, Math.max(0, v));
const smooth = (v: number) => v * v * (3 - 2 * v);
/** 0 before a, rises to 1 at b, holds, falls to 0 from c to d (pass d > 1 to hold to the end). */
const beat = (p: number, a: number, b: number, c: number, d: number) =>
  smooth(clamp((p - a) / (b - a))) * (1 - smooth(clamp((p - c) / (d - c))));

/**
 * Read a pinned section's scroll progress (0 at its top, 1 at its end) as React state.
 * The run is the section height less the pinned stage height. Both are sized in svh, so a
 * phone's address bar showing or hiding during a swipe does not move the progress (#10928).
 * window.innerHeight changes with that bar, which is why this does not use useScroll.
 */
function useSectionProgress(ref: React.RefObject<HTMLElement | null>, stage: React.RefObject<HTMLElement | null>) {
  const [p, setP] = useState(0);
  useEffect(() => {
    let raf = 0;
    const read = () => {
      raf = 0;
      const el = ref.current;
      const st = stage.current;
      if (!el || !st) return;
      const run = el.offsetHeight - st.offsetHeight;
      setP(run > 0 ? clamp(-el.getBoundingClientRect().top / run) : 0);
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(read); };
    read();
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', queue);
      window.removeEventListener('resize', queue);
    };
  }, [ref, stage]);
  return p;
}

/** True while the media query matches. */
function useMedia(query: string) {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const read = () => setOn(mq.matches);
    read();
    mq.addEventListener('change', read);
    return () => mq.removeEventListener('change', read);
  }, [query]);
  return on;
}

/** True on a touch-first device (a phone or tablet): no hover, a finger, no scroll wheel. */
const useCoarsePointer = () => useMedia('(pointer: coarse)');

// --- The sky ----------------------------------------------------------------------------

function rng(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The flight path on a 1440 x 900 box: low on the left, climbing to the upper right. */
function curve(t: number): [number, number] {
  const p0 = [150, 720], p1 = [560, 760], p2 = [930, 260], p3 = [1310, 170];
  const u = 1 - t;
  const x = u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0];
  const y = u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1];
  return [x, y];
}

/** The direction of the flight path at t, in degrees (for the plane's heading). */
function heading(t: number): number {
  const e = 0.001;
  const [x0, y0] = curve(Math.max(0, t - e));
  const [x1, y1] = curve(Math.min(1, t + e));
  return (Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI;
}

const PATH_STARS = 12;
const PATH_D = 'M 150 720 C 560 760, 930 260, 1310 170';

/** The flight at scroll progress p: the sky's stars, how far the contrail has drawn, and the craft. */
function useFlight(p: number) {
  const { field, path } = useMemo(() => {
    const r = rng(7);
    const field = Array.from({ length: 90 }, () => ({ x: r() * 1440, y: r() * 900, s: 0.6 + r() * 1.4, tw: r() * 6 }));
    const path = Array.from({ length: PATH_STARS }, (_, i) => {
      const t = i / (PATH_STARS - 1);
      return { t, from: [r() * 1440, r() * 900] as [number, number], to: curve(t) };
    });
    return { field, path };
  }, []);

  // How far the line has drawn (0 to 1). The plane flies at its tip.
  const drawn = smooth(clamp((p - 0.1) / 0.62));
  // Each star lands on the curve just ahead of the plane.
  const settle = path.map((s) => smooth(clamp((drawn - s.t + 0.22) / 0.22)));
  const pos = path.map((s, i) => [s.from[0] + (s.to[0] - s.from[0]) * settle[i], s.from[1] + (s.to[1] - s.from[1]) * settle[i]]);
  const [px, py] = curve(drawn);
  const planeOn = smooth(clamp(drawn / 0.04));
  // Plane to rocket: the change happens over the upper part of the climb.
  const morph = smooth(clamp((drawn - 0.5) / 0.35));
  const done = clamp((drawn - 0.97) / 0.03);
  // Idle hint before any scroll: the scattered path stars pulse left to right, like a constellation waiting.
  const hint = 1 - smooth(clamp(p / 0.06));
  return { field, settle, pos, px, py, drawn, planeOn, morph, done, hint };
}

function Sky({ p }: { p: number }) {
  const { field, settle, pos, px, py, drawn, planeOn, morph, done, hint } = useFlight(p);
  return (
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 900" preserveAspectRatio="xMaxYMid slice" aria-hidden>
      <defs>
        {/* Bright at the left end (the head), fading to the right (the tail). */}
        <linearGradient id="nf-streak" x1="1" y1="0" x2="0" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="1" stopColor="#fff" stopOpacity="0.9" />
        </linearGradient>
        {/* The contrail: faint where the flight began, bright near the plane. */}
        <linearGradient id="nf-trail" gradientUnits="userSpaceOnUse" x1="150" y1="720" x2="1310" y2="170">
          <stop offset="0" stopColor={BLUE} stopOpacity="0.15" />
          <stop offset="0.6" stopColor={BLUE} stopOpacity="0.75" />
          <stop offset="1" stopColor="#CFE0FF" stopOpacity="1" />
        </linearGradient>
        <filter id="nf-glow" x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>
      {/* Two layers of the field drift slowly in opposite directions, so the sky is never still. */}
      <g className="nf-drift-a">
        {field.filter((_, i) => i % 2 === 0).map((s, i) => (
          <circle key={i} cx={s.x} cy={s.y} r={s.s} fill="#fff" className="nf-twinkle" style={{ animationDelay: `${s.tw}s` }} />
        ))}
      </g>
      <g className="nf-drift-b">
        {field.filter((_, i) => i % 2 === 1).map((s, i) => (
          <circle key={i} cx={s.x} cy={s.y} r={s.s * 0.8} fill="#fff" className="nf-twinkle" style={{ animationDelay: `${s.tw + 1.5}s` }} />
        ))}
      </g>
      {/* A rare shooting star, calm not flashy: three, staggered across an 18 s cycle. */}
      {[
        { x: 1180, y: 90, d: 2 },
        { x: 820, y: 60, d: 8.5 },
        { x: 1360, y: 260, d: 14 },
      ].map((s, i) => (
        // The streak points the way it travels (down and to the left): tail up-right, head down-left.
        <line key={i} x1={s.x + 90} y1={s.y - 48} x2={s.x} y2={s.y} stroke="url(#nf-streak)" strokeWidth={1.6} strokeLinecap="round" className="nf-shoot" style={{ animationDelay: `${s.d}s` }} />
      ))}
      {/* The contrail: one smooth curve that draws as you scroll, with a soft glow under it. */}
      <path d={PATH_D} pathLength={1} fill="none" stroke={BLUE} strokeWidth={10} strokeOpacity={0.35} strokeLinecap="round" strokeDasharray="1 1" strokeDashoffset={1 - drawn} filter="url(#nf-glow)" />
      <path d={PATH_D} pathLength={1} fill="none" stroke="url(#nf-trail)" strokeWidth={2.2} strokeLinecap="round" strokeDasharray="1 1" strokeDashoffset={1 - drawn} />
      {/* Once the flight is complete, a small light travels the path, over and over. */}
      <path d={PATH_D} fill="none" stroke="#CFE0FF" strokeWidth={3} strokeLinecap="round" strokeDasharray="10 1600" className="nf-travel" opacity={done * 0.8} />
      {pos.map((c, i) => (
        <g key={i}>
          <circle cx={c[0]} cy={c[1]} r={1.8 + settle[i] * 1.6} fill="#fff" opacity={0.55 + settle[i] * 0.45} />
          {/* The waiting pulse: a soft halo that runs left to right while nobody has scrolled yet. */}
          <g opacity={hint}>
            <circle cx={c[0]} cy={c[1]} r={7} fill="#9CC2FF" className="nf-hint" style={{ animationDelay: `${i * 0.28}s` }} />
          </g>
        </g>
      ))}
      {/* The craft flies at the tip of the contrail and turns with the path. It starts as a paper
          plane and, as it climbs, folds into a small rocket: an idea becoming a full launch. */}
      <g transform={`translate(${px} ${py}) rotate(${heading(drawn)}) scale(1.45)`} opacity={planeOn}>
        <g className={done > 0.5 ? 'nf-bob' : undefined}>
          <ellipse cx={-4} cy={0} rx={34} ry={14} fill="#9CC2FF" opacity={0.25} filter="url(#nf-glow)" />
          {/* A soft flash at the moment of change. */}
          <ellipse cx={0} cy={0} rx={30} ry={18} fill="#CFE0FF" opacity={Math.sin(Math.PI * morph) * 0.55} filter="url(#nf-glow)" />
          {/* The paper plane folds flat as the rocket takes shape. Nose points along +x. */}
          <g transform={`scale(${1 - morph * 0.3} ${1 - morph * 0.85})`} opacity={1 - morph}>
            <path d="M 30 0 L -20 -15 L -9 0 Z" fill="#FFFFFF" />
            <path d="M 30 0 L -9 0 L -16 13 Z" fill="#C9D8F5" />
            <path d="M 30 0 L -9 0 L -5 5 Z" fill="#8FAEE6" />
          </g>
          <g transform={`scale(${0.7 + morph * 0.3} ${0.3 + morph * 0.7})`} opacity={morph}>
            {/* Flame, fins, body, window. */}
            <path d="M -14 -4.5 L -32 0 L -14 4.5 Z" fill="#F5C542" className="nf-flame" />
            <path d="M -14 -2 L -24 0 L -14 2 Z" fill="#FFFFFF" className="nf-flame" />
            <path d="M -8 -6.5 L -19 -15 L -15 -5.5 Z" fill="#8FAEE6" />
            <path d="M -8 6.5 L -19 15 L -15 5.5 Z" fill="#8FAEE6" />
            <path d="M 28 0 C 22 -8.5, 6 -8.5, -15 -7 L -15 7 C 6 8.5, 22 8.5, 28 0 Z" fill="#FFFFFF" />
            <path d="M -15 -7 L -15 7 L -11 7 L -11 -7 Z" fill="#C9D8F5" />
            <circle cx={9} cy={0} r={3.4} fill={BLUE} stroke="#CFE0FF" strokeWidth={1.2} />
          </g>
        </g>
      </g>
    </svg>
  );
}

/** A few sparkles and clouds scattered on the doodle paper (fixed places, so the page is calm). */
// Kept off the left half below the header, where the hero text sits.
const DOODLE_SPARKLES = [
  { x: 120, y: 110, r: 11 }, { x: 520, y: 100, r: 8 }, { x: 860, y: 140, r: 10 }, { x: 1080, y: 60, r: 8 },
  { x: 1390, y: 340, r: 11 }, { x: 1210, y: 620, r: 8 }, { x: 900, y: 820, r: 11 }, { x: 1400, y: 840, r: 8 },
];
const DOODLE_CLOUDS = [
  { x: 1180, y: 150, s: 1.4, d: 'a' }, { x: 1100, y: 470, s: 1.1, d: 'b' }, { x: 700, y: 800, s: 1, d: 'a' },
];

/** The light-mode hero: the same flight, drawn as a doodle on paper. */
function DoodleSky({ p }: { p: number }) {
  const { settle, pos, px, py, drawn, planeOn, morph, done, hint } = useFlight(p);
  return (
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 900" preserveAspectRatio="xMaxYMid slice" aria-hidden>
      <defs>
        {/* Reveals the dashed pencil line as the plane draws it. */}
        <mask id="nf-d-reveal" maskUnits="userSpaceOnUse" x="0" y="0" width="1440" height="900">
          <path d={PATH_D} pathLength={1} fill="none" stroke="#fff" strokeWidth={18} strokeLinecap="round" strokeDasharray="1 1" strokeDashoffset={1 - drawn} />
        </mask>
      </defs>
      {DOODLE_CLOUDS.map((c, i) => (
        <g key={i} className={c.d === 'a' ? 'nf-drift-a' : 'nf-drift-b'}>
          <path d={CLOUD_D} transform={`translate(${c.x} ${c.y}) scale(${c.s})`} fill="#FFFFFF" {...OUT} />
        </g>
      ))}
      {DOODLE_SPARKLES.map((s, i) => (
        <path key={i} d={sparkle(s.r)} transform={`translate(${s.x} ${s.y})`} fill="#FFFFFF" className="nf-twinkle" style={{ animationDelay: `${i * 0.6}s` }} {...OUT} />
      ))}
      {/* The contrail: a dashed pencil line. */}
      <path d={PATH_D} fill="none" stroke={INK} strokeWidth={3} strokeLinecap="round" strokeDasharray="14 12" mask="url(#nf-d-reveal)" />
      {/* Once the flight is complete, a small yellow dash travels the path, over and over. */}
      <path d={PATH_D} fill="none" stroke="#F5C542" strokeWidth={6} strokeLinecap="round" strokeDasharray="10 1600" className="nf-travel" opacity={done} />
      {pos.map((c, i) => (
        <g key={i} transform={`translate(${c[0]} ${c[1]})`}>
          <path d={sparkle(5 + settle[i] * 5)} fill={settle[i] > 0.5 ? '#F5C542' : '#FFFFFF'} {...OUT} />
          <g opacity={hint}>
            <circle r={12} fill="none" stroke={INK} strokeWidth={1.5} strokeDasharray="3 4" className="nf-hint" style={{ animationDelay: `${i * 0.28}s` }} />
          </g>
        </g>
      ))}
      <g transform={`translate(${px} ${py}) rotate(${heading(drawn)}) scale(2.2)`} opacity={planeOn}>
        <g className={done > 0.5 ? 'nf-bob' : undefined}>
          <g transform={`scale(${1 - morph * 0.3} ${1 - morph * 0.85})`} opacity={1 - morph}>
            <DoodlePlane />
          </g>
          <g transform={`scale(${0.7 + morph * 0.3} ${0.3 + morph * 0.7})`} opacity={morph}>
            <DoodleRocket />
          </g>
        </g>
      </g>
    </svg>
  );
}


/** One text beat. `plain` drops the blur: a per-frame blur filter stutters on phone GPUs (#10928). */
function Beat({ v, children, className = '', plain = false }: { v: number; children: React.ReactNode; className?: string; plain?: boolean }) {
  return (
    <div
      className={`absolute inset-x-0 ${className}`}
      style={{ opacity: v, filter: plain ? undefined : `blur(${(1 - v) * 10}px)`, transform: `translateY(${(1 - v) * 12}px)`, pointerEvents: v > 0.5 ? 'auto' : 'none' }}
      aria-hidden={v < 0.05}
    >
      {children}
    </div>
  );
}

function ConsultButton() {
  return (
    <a href="/get-started" className="group inline-flex items-center gap-2 px-8 py-4 card-primary-cta rounded-xl font-semibold text-lg sbi-shadow-md" {...ctaAttrs(getStartedCtaLabel('home-hero'))}>
      Book a Free Consult
      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden />
    </a>
  );
}

const WORDS = [
  { word: 'Strategy.', line: 'We build the plan with you.' },
  { word: 'Execution.', line: 'Then we help run it. Not a slide deck handed over at the door.' },
];

export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotionConfig();
  const coarse = useCoarsePointer();
  const live = useSectionProgress(ref, stage);
  const p = reduce ? 1 : live;

  const intro = reduce ? 0 : beat(p, -1, 0, 0.16, 0.24);
  const words = [beat(p, 0.24, 0.3, 0.4, 0.46), beat(p, 0.46, 0.52, 0.62, 0.68)];
  const end = beat(p, 0.7, 0.8, 2, 3);
  const dark = useDark();
  const soft = dark ? 'text-white/80' : 'nf-ink-soft';

  // svh, not vh: a phone's address bar does not resize the section mid-swipe. On a touch screen
  // the run is longer, so one flick does not carry past the whole story (#10928).
  return (
    <section ref={ref} className={`relative ${reduce ? '' : coarse ? 'h-[380svh]' : 'h-[260svh]'}`} style={{ background: dark ? '#050B1F' : 'var(--nf-page)' }}>
      <style>{`
        @keyframes nf-tw{0%,100%{opacity:.35}50%{opacity:1}}
        .nf-twinkle{animation:nf-tw 6s ease-in-out infinite}
        @keyframes nf-da{from{transform:translate(0,0)}to{transform:translate(-36px,14px)}}
        @keyframes nf-db{from{transform:translate(0,0)}to{transform:translate(28px,-10px)}}
        .nf-drift-a{animation:nf-da 40s ease-in-out infinite alternate}
        .nf-drift-b{animation:nf-db 55s ease-in-out infinite alternate}
        @keyframes nf-shoot{0%{transform:translate(0,0);opacity:0}1%{opacity:1}6%{transform:translate(-380px,200px);opacity:0}100%{transform:translate(-380px,200px);opacity:0}}
        .nf-shoot{opacity:0;animation:nf-shoot 18s ease-out infinite}
        @keyframes nf-hint{0%,100%{transform:scale(.4);opacity:0}30%{transform:scale(1);opacity:.35}60%{transform:scale(1.3);opacity:0}}
        .nf-hint{transform-box:fill-box;transform-origin:center;animation:nf-hint 3.4s ease-in-out infinite}
        @keyframes nf-travel{from{stroke-dashoffset:1610}to{stroke-dashoffset:0}}
        .nf-travel{animation:nf-travel 5s ease-in-out infinite}
        @keyframes nf-bob{0%,100%{transform:translate(0,0) rotate(0)}50%{transform:translate(3px,-5px) rotate(-3deg)}}
        .nf-bob{animation:nf-bob 3.2s ease-in-out infinite}
        @keyframes nf-cue{0%,100%{transform:translateY(0);opacity:.5}50%{transform:translateY(6px);opacity:1}}
        .nf-cue{animation:nf-cue 1.8s ease-in-out infinite}
        @keyframes nf-flame{0%,100%{transform:scaleX(.85)}50%{transform:scaleX(1.15)}}
        .nf-flame{transform-box:fill-box;transform-origin:100% 50%;animation:nf-flame .5s ease-in-out infinite}
        @media (prefers-reduced-motion:reduce){.nf-twinkle,.nf-drift-a,.nf-drift-b,.nf-shoot,.nf-hint,.nf-travel,.nf-bob,.nf-cue,.nf-flame{animation:none}.nf-shoot,.nf-hint{display:none}}
      `}</style>
      <PageSky />
      <div
        ref={stage}
        className={`sticky top-0 h-svh overflow-hidden ${dark ? 'text-white' : 'nf-ink'}`}
        style={{ background: dark ? 'radial-gradient(120% 90% at 70% 10%, #0E2350 0%, #050B1F 60%)' : `${PAPER_DOTS}, var(--nf-page)` }}
      >
        {dark ? <Sky p={p} /> : <DoodleSky p={p} />}
        <div className="relative h-full max-w-6xl mx-auto px-6 sm:px-10">
          <Beat v={intro} plain={coarse} className="top-[22%] px-6 sm:px-10">
            <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: dark ? BLUE : 'var(--nf-accent)' }}>Strategy. Execution. Momentum.</p>
            <h1 className="mt-4 text-5xl sm:text-7xl font-bold font-heading leading-[1.05] max-w-3xl">Strategy + Execution. One&nbsp;Partner.</h1>
            <div className="mt-10"><ConsultButton /></div>
            <p className={`mt-10 inline-flex flex-col items-center gap-1 text-sm ${dark ? 'text-white/60' : 'nf-ink-faint'}`}>
              Scroll
              <span className={`nf-cue block h-5 w-px ${dark ? 'bg-white/60' : 'bg-current'}`} aria-hidden />
            </p>
          </Beat>
          {WORDS.map((w, i) => (
            <Beat key={w.word} v={words[i]} plain={coarse} className="top-[34%] px-6 sm:px-10">
              <p className="text-5xl sm:text-8xl font-bold font-heading">{w.word}</p>
              <p className={`mt-5 text-xl sm:text-2xl max-w-xl ${soft}`}>{w.line}</p>
            </Beat>
          ))}
          <Beat v={end} plain={coarse} className="top-[30%] px-6 sm:px-10">
            <p className="text-5xl sm:text-8xl font-bold font-heading">Momentum.</p>
            <p className={`mt-5 text-xl sm:text-2xl max-w-xl ${soft}`}>We fly it with you.</p>
            <div className="mt-10"><ConsultButton /></div>
          </Beat>
          {reduce && <h1 className="sr-only">Strategy + Execution. One Partner.</h1>}
        </div>
      </div>
    </section>
  );
}

/** The live strip: the next event, the latest field note and a featured client quote (#10765). */
export function Strip() {
  return <LiveStrip />;
}

// --- Growth: the orbit -------------------------------------------------------------------

/**
 * The endless orbit. It drifts on its own, one area every few seconds. With the cursor over the
 * orbit, the drift stops and the scroll wheel turns it instead, round and round with no end.
 * Move the cursor off the orbit and the wheel scrolls the page again. Click an area to bring it
 * to the front. On a touch screen a left or right swipe turns it and settles on an area; an up
 * or down swipe still scrolls the page (touch-action: pan-y); a tap picks an area (#10928).
 */
const DRIFT = 0.22; // areas per second while idle
const WHEEL_PER_AREA = 140; // wheel pixels to move one area
const SWIPE_PER_AREA = 70; // finger pixels to move one area
const SWIPE_START = 8; // finger pixels before a press counts as a swipe, not a tap

function useOrbit(area: React.RefObject<HTMLDivElement | null>, n: number, off: boolean) {
  const [turn, setTurn] = useState(0);
  const target = useRef(0);
  const shown = useRef(0);
  const hover = useRef(false);
  const lastWheel = useRef(0);
  const swipe = useRef<{ id: number; x: number; y: number; from: number; on: boolean } | null>(null);
  const swiped = useRef(false);

  useEffect(() => {
    if (off) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const idle = !hover.current && !swipe.current && now - lastWheel.current > 1200;
      if (idle) target.current += dt * DRIFT;
      shown.current += (target.current - shown.current) * Math.min(1, dt * 6);
      setTurn(shown.current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [off]);

  useEffect(() => {
    const el = area.current;
    if (!el || off) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      lastWheel.current = performance.now();
      target.current += e.deltaY / WHEEL_PER_AREA;
    };
    const enter = (e: PointerEvent) => { if (e.pointerType === 'mouse') hover.current = true; };
    const leave = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      hover.current = false;
      target.current = Math.round(target.current); // settle on an area before drifting on
    };
    // Touch and pen: a sideways swipe turns the orbit. The browser keeps vertical swipes for
    // the page scroll (touch-action: pan-y on the orbit), and then sends pointercancel.
    const down = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      swiped.current = false;
      swipe.current = { id: e.pointerId, x: e.clientX, y: e.clientY, from: target.current, on: false };
    };
    const move = (e: PointerEvent) => {
      const s = swipe.current;
      if (!s || s.id !== e.pointerId) return;
      const dx = e.clientX - s.x;
      if (!s.on) {
        if (Math.abs(dx) < SWIPE_START || Math.abs(dx) < Math.abs(e.clientY - s.y)) return;
        s.on = true;
        swiped.current = true;
      }
      target.current = s.from + dx / SWIPE_PER_AREA;
      lastWheel.current = performance.now();
    };
    const end = (e: PointerEvent) => {
      const s = swipe.current;
      if (!s || s.id !== e.pointerId) return;
      if (s.on) target.current = Math.round(target.current); // settle on an area
      swipe.current = null;
      lastWheel.current = performance.now();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerenter', enter);
    el.addEventListener('pointerleave', leave);
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerenter', enter);
      el.removeEventListener('pointerleave', leave);
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', end);
      el.removeEventListener('pointercancel', end);
    };
  }, [area, off]);

  /** Bring area i to the front by the shortest way round. A tap that ended a swipe picks nothing. */
  const pick = (i: number) => {
    if (swiped.current) { swiped.current = false; return; }
    const cur = target.current;
    target.current = i + n * Math.round((cur - i) / n);
    lastWheel.current = performance.now();
  };
  return { turn, pick };
}

export function Growth() {
  const services = getActiveServices();
  const area = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotionConfig();
  const n = services.length;
  const { turn, pick } = useOrbit(area, n, !!reduce);
  const coarse = useCoarsePointer();
  // On a phone-width screen a narrower orbit keeps the side pills on screen (#10928).
  const narrow = useMedia('(max-width: 639px)');
  const rx = narrow ? 30 : 42;

  if (reduce) {
    return (
      <section className="py-24 px-6 sm:px-10 bg-background">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold font-heading text-primary">Business Growth</h2>
          <ul className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-6">
            {services.map((s) => (
              <li key={s.id}>
                <a href={`/services#${s.slug}`} className="text-2xl font-bold font-heading hover:text-primary">{s.name}</a>
                <p className="mt-1 text-foreground/75">{s.tagline}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    );
  }

  // The front is the bottom of the ellipse. `turn` grows without end, so the orbit loops.
  const items = services.map((s, i) => {
    const a = ((i - turn) / n) * Math.PI * 2 + Math.PI / 2;
    const z = (Math.sin(a) + 1) / 2;
    return { s, x: Math.cos(a), y: Math.sin(a), z };
  });
  const active = items.reduce((m, it) => (it.z > m.z ? it : m), items[0]).s;

  return (
    <section className="relative nf-ink">
      <NfTheme />
      <div className="min-h-screen py-24 overflow-hidden flex items-center">
        <div className="max-w-6xl mx-auto w-full px-6 sm:px-10 grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-8 items-center">
          <div className="relative z-10">
            <h2 className="text-4xl sm:text-5xl font-bold font-heading nf-accent">Business Growth</h2>
            <p className="mt-4 text-lg nf-ink-soft max-w-md">Strategy and hands-on support across the whole business.</p>
            <motion.div key={active.id} className="mt-8" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeInOut' }}>
              <p className="text-3xl sm:text-4xl font-bold font-heading">{active.name}</p>
              <p className="mt-2 text-lg nf-accent font-medium">{active.tagline}</p>
              <a href={`/services#${active.slug}`} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold nf-ink-soft nf-hover-accent">
                Explore {active.name} <ArrowRight className="w-4 h-4" aria-hidden />
              </a>
            </motion.div>
          </div>
          {/* pan-y: the browser keeps up and down swipes for the page; sideways swipes turn the orbit. */}
          <div ref={area} className="relative h-[42svh] sm:h-[56svh] cursor-ns-resize select-none" style={{ touchAction: 'pan-y' }} aria-hidden>
            {coarse && (
              <p className="absolute inset-x-0 bottom-0 text-center text-xs font-semibold uppercase tracking-[0.16em] nf-ink-faint">Swipe to turn</p>
            )}
            <span className="absolute top-1/2 h-[44%] -translate-y-1/2 rounded-[50%] border nf-orbit" style={{ borderColor: 'var(--nf-line)', left: `${50 - rx}%`, right: `${50 - rx}%` }} />
            {items.map(({ s, x, y, z }, i) => (
              <span
                key={s.id}
                onClick={() => pick(i)}
                className={`absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border nf-pill px-5 py-2.5 text-base sm:text-xl font-semibold font-heading cursor-pointer nf-ink ${s.id === active.id ? 'nf-pill-on' : ''}`}
                style={{
                  borderColor: s.id === active.id ? BLUE : 'var(--nf-line)',
                  background: s.id === active.id ? 'var(--nf-pill)' : 'var(--nf-glass)',
                  left: `${50 + x * rx}%`,
                  top: `${50 + y * 22}%`,
                  zIndex: Math.round(z * 10),
                  opacity: 0.25 + z * 0.75,
                  transform: `translate(-50%, -50%) scale(${0.65 + z * 0.55})`,
                  filter: `blur(${(1 - z) * 2.5}px)`,
                }}
              >
                {s.name}
              </span>
            ))}
          </div>
        </div>
        <ul className="sr-only">
          {services.map((s) => (
            <li key={s.id}><a href={`/services#${s.slug}`}>{s.name}: {s.tagline}</a></li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// --- Footer: the landing ------------------------------------------------------------------
// The night sky carries on. The contrail comes in from the upper left and the plane lands by
// the last call to action. Then the current footer's cards, links and legal row, restyled on
// the same sky. Copy and links are the current footer's (apps/www-sbi .../Footer.tsx).

const CARDS = [
  { title: 'SmallBiz Programs', text: 'Our flagship offer. From $399/mo.', href: '/programs', cta: 'Explore Programs', lead: true },
  { title: 'Get Started', text: 'Ready to grow? Start your journey here.', href: '/get-started', cta: 'Start Now' },
  { title: 'Free Consults', text: 'Book a free virtual strategy session.', href: '/free-consults', cta: 'Book Now' },
  { title: 'Newsletter', text: 'Get tips, insights, and updates delivered.', href: '/newsletter', cta: 'Subscribe' },
  { title: 'Contact', text: 'Have questions? Chat with our team.', href: '/contact', cta: 'Get in Touch' },
];

type FooterLink = { label: string; href?: string; strong?: boolean; external?: boolean };
const COLUMNS: { title: string; links: FooterLink[] }[] = [
  {
    title: 'Business Services',
    links: [
      { label: 'SmallBiz Programs', href: '/programs', strong: true },
      { label: 'All Services', href: '/services' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'On-Demand Support', href: '/on-demand-support' },
      { label: 'Virtual Strategy Sessions', href: '/virtual-strategy-sessions' },
    ],
  },
  {
    title: 'About SmallBiz',
    links: [
      { label: 'SmallBiz Innovation', href: '/about' },
      { label: 'Company Updates', href: '/updates' },
      { label: 'Testimonials', href: '/testimonials' },
      { label: 'Policies', href: '/policies' },
      { label: 'FAQs', href: '/faqs' },
    ],
  },
  { title: 'Client Services', links: [{ label: 'SmallBiz Support Desk', href: '/support-desk' }] },
  {
    title: 'Get In Touch',
    links: [
      { label: 'SmallBiz Innovation LLC', strong: true },
      { label: '(650) 200-0828', href: 'tel:+16502000828' },
      { label: 'hello@smallbizinnovation.com', href: 'mailto:hello@smallbizinnovation.com' },
      { label: 'Contact / Open Chat', href: '/contact' },
    ],
  },
  {
    title: 'Free Resources',
    links: [
      { label: 'Download Freebies', href: '/freebies' },
      { label: 'SBI Pulse - Free Newsletter!', href: '/newsletter' },
      { label: 'SmallBiz ToolBox - Our Recs & Deals', href: '/toolbox' },
      { label: 'SmallBiz BotStudio - Free Gen AI Tools', href: 'https://botstudio.smallbizinnovation.com', external: true },
      { label: 'Virtual Strategy Session - Free Consults!', href: '/free-consults' },
    ],
  },
];

const LEGAL = [
  ['Contact', '/contact'],
  ['Policies', '/policies'],
  ['Terms', '/terms'],
  ['Privacy', '/privacy'],
  ['Cookies', '/cookies'],
  ['Refund', '/refund'],
  ['Billing', '/billing'],
];

/** The approach: a descending curve on a 1440 x 360 box, ending level at the landing point. */
const LANDING_D = 'M -40 18 C 640 -4, 1000 300, 1300 286';

/** The light-mode landing: a dashed pencil approach and the doodle plane, on paper. */
function DoodleLandingSky() {
  const reduce = useReducedMotionConfig();
  return (
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 360" preserveAspectRatio="xMaxYMid slice" aria-hidden>
      <defs>
        <mask id="nf-d-land" maskUnits="userSpaceOnUse" x="-60" y="-40" width="1560" height="440">
          <motion.path
            d={LANDING_D} fill="none" stroke="#fff" strokeWidth={18} strokeLinecap="round"
            initial={reduce ? false : { pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 2.2, ease: 'easeInOut' }}
          />
        </mask>
      </defs>
      {[{ x: 300, y: 70, r: 8 }, { x: 760, y: 40, r: 6 }, { x: 1120, y: 120, r: 9 }, { x: 980, y: 300, r: 6 }].map((s, i) => (
        <path key={i} d={sparkle(s.r)} transform={`translate(${s.x} ${s.y})`} fill="#FFFFFF" className="nf-twinkle" style={{ animationDelay: `${i * 0.8}s` }} {...OUT} />
      ))}
      <path d={CLOUD_D} transform="translate(1180 30) scale(1.1)" fill="#FFFFFF" className="nf-drift-b" {...OUT} />
      <path d={LANDING_D} fill="none" stroke={INK} strokeWidth={3} strokeLinecap="round" strokeDasharray="14 12" mask="url(#nf-d-land)" />
      <motion.g
        initial={reduce ? false : { opacity: 0, x: -60, y: -6 }} whileInView={{ opacity: 1, x: 0, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.9, delay: reduce ? 0 : 1.6, ease: 'easeInOut' }}
      >
        <g transform="translate(1300 286) rotate(-2) scale(1.45)">
          <g className="nf-bob"><DoodlePlane /></g>
        </g>
      </motion.g>
    </svg>
  );
}

function LandingSky() {
  const reduce = useReducedMotionConfig();
  const stars = useMemo(() => {
    const r = rng(23);
    return Array.from({ length: 60 }, () => ({ x: r() * 1440, y: r() * 360, s: 0.5 + r() * 1.2, tw: r() * 6 }));
  }, []);
  return (
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 360" preserveAspectRatio="xMaxYMid slice" aria-hidden>
      <defs>
        <linearGradient id="nf-land" gradientUnits="userSpaceOnUse" x1="-40" y1="40" x2="1300" y2="286">
          <stop offset="0" stopColor={BLUE} stopOpacity="0.1" />
          <stop offset="0.7" stopColor={BLUE} stopOpacity="0.7" />
          <stop offset="1" stopOpacity="1" style={{ stopColor: 'var(--nf-trail-end, #CFE0FF)' }} />
        </linearGradient>
        <filter id="nf-land-glow" x="-10%" y="-20%" width="120%" height="140%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>
      <g className="nf-drift-a" style={{ opacity: 'var(--nf-star-alpha, 1)' }}>
        {stars.map((s, i) => (
          <circle key={i} cx={s.x} cy={s.y} r={s.s} className="nf-twinkle" style={{ fill: 'var(--nf-star, #fff)', animationDelay: `${s.tw}s` }} />
        ))}
      </g>
      {/* The contrail draws in when the footer comes into view. */}
      <motion.path
        d={LANDING_D} fill="none" stroke={BLUE} strokeWidth={10} strokeOpacity={0.3} strokeLinecap="round" filter="url(#nf-land-glow)"
        initial={reduce ? false : { pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 2.2, ease: 'easeInOut' }}
      />
      <motion.path
        d={LANDING_D} fill="none" stroke="url(#nf-land)" strokeWidth={2.2} strokeLinecap="round"
        initial={reduce ? false : { pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 2.2, ease: 'easeInOut' }}
      />
      {/* The plane settles at the end of the approach, then rides the air gently. */}
      <motion.g
        initial={reduce ? false : { opacity: 0, x: -60, y: -6 }} whileInView={{ opacity: 1, x: 0, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.9, delay: reduce ? 0 : 1.6, ease: 'easeInOut' }}
      >
        <g transform="translate(1300 286) rotate(-2) scale(1.45)">
          <g className="nf-bob">
            <ellipse cx={-4} cy={0} rx={34} ry={14} opacity={0.25} filter="url(#nf-land-glow)" style={{ fill: 'var(--nf-plane-glow, #9CC2FF)' }} />
            <path d="M 30 0 L -20 -15 L -9 0 Z" style={{ fill: 'var(--nf-plane-1, #FFFFFF)', stroke: 'var(--nf-plane-edge, transparent)', strokeWidth: 0.8 }} />
            <path d="M 30 0 L -9 0 L -16 13 Z" style={{ fill: 'var(--nf-plane-2, #C9D8F5)' }} />
            <path d="M 30 0 L -9 0 L -5 5 Z" style={{ fill: 'var(--nf-plane-3, #8FAEE6)' }} />
          </g>
        </g>
      </motion.g>
    </svg>
  );
}

/** The loud yellow call-to-action section moves into the footer landing, on the calm sky. */
export function Cta() {
  return null;
}

export function Footer() {
  const year = new Date().getFullYear();
  const dark = useDark();
  return (
    <footer className="relative nf-ink overflow-hidden" style={{ background: 'var(--nf-paper-dots), var(--nf-foot)' }}>
      <NfTheme />
      {/* The landing */}
      <div className="relative h-[520px] sm:h-[420px]">
        {dark ? <LandingSky /> : <DoodleLandingSky />}
        <div className="relative h-full max-w-7xl mx-auto px-6 sm:px-10 flex flex-col justify-start pt-10 sm:justify-center sm:pt-0">
          <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: dark ? BLUE : 'var(--nf-accent)' }}>Strategy. Execution. Momentum.</p>
          <p className="mt-3 text-4xl sm:text-5xl font-bold font-heading max-w-2xl">Stop Planning. Start Executing.</p>
          <p className="mt-4 text-lg nf-ink-soft max-w-xl">Full-stack strategy and execution. One partner to help you build, grow, and scale your business.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            {/* The call for attention: a slow golden glow behind the button, not a loud band of colour. */}
            <span className="relative inline-flex w-fit">
              <span aria-hidden className="nf-pulse nf-glow-only absolute -inset-3 rounded-2xl" style={{ background: 'radial-gradient(closest-side, rgba(245,197,66,0.45), rgba(245,197,66,0))' }} />
              <a href="/get-started" className="group relative inline-flex items-center gap-2 px-8 py-4 card-primary-cta rounded-xl font-semibold text-lg sbi-shadow-md" {...ctaAttrs(getStartedCtaLabel('home-bottom'))}>
                Work With Us <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden />
              </a>
            </span>
            <a href="/services" className="nf-ghost inline-flex w-fit items-center px-8 py-4 rounded-xl font-semibold text-lg border transition-colors">Explore Services</a>
          </div>
          <style>{`@keyframes nf-pulse{0%,100%{opacity:.35;transform:scale(.96)}50%{opacity:.9;transform:scale(1.04)}}.nf-pulse{animation:nf-pulse 4s ease-in-out infinite}@media (prefers-reduced-motion:reduce){.nf-pulse{animation:none;opacity:.6}}`}</style>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 pb-10">
        {/* The quick cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 pb-12 border-b" style={{ borderColor: 'var(--nf-line)' }}>
          {CARDS.map((c) => (
            <a
              key={c.title}
              href={c.href}
              className={`group block p-6 rounded-2xl border backdrop-blur-sm transition-colors ${c.lead ? 'nf-card nf-lead' : 'nf-card'} hover:border-sunshine/60`}
            >
              <p className="text-lg font-bold font-heading nf-group-gold transition-colors">{c.title}</p>
              <p className="mt-2 text-sm nf-ink-soft">{c.text}</p>
              <p className="mt-4 inline-flex items-center gap-1 text-sm font-semibold nf-gold">
                {c.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden />
              </p>
            </a>
          ))}
        </div>

        {/* The link columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 py-12">
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <p className="text-sm font-semibold uppercase tracking-[0.14em] nf-ink-faint">{col.title}</p>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label} className="text-sm [overflow-wrap:anywhere]">
                    {l.href ? (
                      <a
                        href={l.href}
                        {...(l.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        className={`${l.strong ? 'nf-gold font-semibold' : 'nf-ink-soft'} nf-hover-gold transition-colors`}
                      >
                        {l.label}
                      </a>
                    ) : (
                      <span className="nf-ink font-semibold">{l.label}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* The legal row */}
        <div className="pt-8 border-t flex flex-col md:flex-row items-center justify-between gap-4 text-sm" style={{ borderColor: 'var(--nf-line)' }}>
          <div className="text-center md:text-left">
            <p className="font-bold font-heading">SmallBiz Innovation</p>
            <p className="nf-ink-faint">&copy;{year} SmallBiz Innovation LLC</p>
          </div>
          <nav aria-label="Legal" className="flex flex-wrap justify-center gap-x-5 gap-y-2">
            {LEGAL.map(([label, href]) => (
              <a key={href} href={href} className="nf-ink-faint nf-hover-gold transition-colors">{label}</a>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
}

// --- Positioning: the embedded partnership line --------------------------------------------
// Replaces the freelancers / agencies / coaches comparison, which talked down to people we work
// with. The headline is canon: VOICE_RULES.md 55 ("We work inside your business, not for it.").

export function Positioning() {
  const reduce = useReducedMotionConfig();
  return (
    <section className="py-24 sm:py-32 px-6 sm:px-10">
      <NfTheme />
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] nf-accent">Why SmallBiz Innovation</p>
        <motion.h2
          className="mt-5 text-4xl sm:text-6xl font-bold font-heading leading-[1.1] nf-ink"
          initial={reduce ? false : { opacity: 0, y: 16, filter: 'blur(6px)' }}
          whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        >
          We work <span className="nf-accent">inside</span> your business, not for it.
        </motion.h2>
        <p className="mt-6 text-lg sm:text-xl nf-ink-soft max-w-2xl mx-auto">
          Your partner in strategy and execution across marketing, operations, finance, and systems.
        </p>
      </div>
    </section>
  );
}

// --- Header: one floating bar that splits into two globs ----------------------------------
// At the top of the page the header is one floating glass bar. As you scroll, the bar's middle
// (the section links) fades away and the two ends become separate floating globs: the logo on
// the left, the actions on the right, like the app's own header (app-sbs AppLayout, the left
// glob). A menu button in the left glob opens the links. Links are the site header's sections.

const NAV: { title: string; links: [string, string][] }[] = [
  { title: 'Company', links: [['About', '/about'], ['Services', '/services'], ['Testimonials', '/testimonials'], ['Portfolio', '/portfolio'], ['Contact', '/contact']] },
  { title: 'Services', links: [['SmallBiz Programs', '/programs'], ['Support Desk', '/support-desk'], ['On-Demand Support', '/on-demand-support'], ['Virtual Strategy Sessions', '/virtual-strategy-sessions'], ['Free Consults', '/free-consults'], ['Pricing', '/pricing']] },
  { title: 'Resources', links: [['Blog', '/blog'], ['Glossary', '/glossary'], ['Toolbox', '/toolbox'], ['Updates', '/updates'], ['Freebies', '/freebies'], ['Newsletter', '/newsletter']] },
  { title: 'Expertise', links: [['Marketing', '/marketing'], ['Sales', '/sales'], ['Automation', '/automation'], ['Finance', '/finance'], ['Leadership', '/leadership']] },
];

export function Header() {
  const [split, setSplit] = useState(false);
  const [menu, setMenu] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    const on = () => setSplit(window.scrollY > 80);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  useEffect(() => {
    if (!split) setMenu(false);
  }, [split]);

  // One glass surface behind the whole bar fades out as two glass globs fade in at the ends.
  const ease = 'transition-all duration-500 ease-in-out';
  const glass = 'glass-morphism border border-border/20';
  return (
    <header className="nf-header fixed top-0 inset-x-0 z-40 px-2 sm:px-4 pt-2">
      <NfTheme />
      <div className="relative max-w-7xl xl:max-w-[1400px] mx-auto">
        <div aria-hidden className={`absolute inset-0 rounded-2xl sbi-shadow-md ${glass} ${ease} ${split ? 'opacity-0 scale-x-[0.98]' : 'opacity-100'}`} />
        <div className="relative flex items-center justify-between gap-3">
          {/* Left glob: menu + logo */}
          <div className={`relative flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 rounded-2xl ${ease} ${split ? `${glass} sbi-shadow-lg translate-y-1` : 'border border-transparent'}`}>
            <button
              type="button"
              onClick={() => setMenu((m) => !m)}
              className={`p-2.5 rounded-lg text-foreground/80 hover:text-primary ${split ? '' : 'md:hidden'}`}
              aria-label="Open menu"
              aria-expanded={menu}
            >
              {menu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <a href="/" className="flex items-center gap-2 px-1">
              <SBILogo size={32} className="w-8 h-8 sm:w-9 sm:h-9" />
              <span className={`font-bold text-sm sm:text-base whitespace-nowrap overflow-hidden ${ease} ${split ? 'max-w-0 opacity-0 lg:max-w-[200px] lg:opacity-100' : 'max-w-[200px] opacity-100'}`}>
                <span className="hidden lg:inline">SmallBiz Innovation</span>
                <span className="lg:hidden">SBI</span>
              </span>
            </a>
          </div>

          {/* The middle: section links, only while the bar is whole */}
          <nav aria-label="Main" className={`hidden md:flex items-center gap-1 ${ease} ${split ? 'opacity-0 pointer-events-none -translate-y-1' : 'opacity-100'}`}>
            {NAV.map((s) => (
              <div key={s.title} className="relative" onMouseEnter={() => setOpen(s.title)} onMouseLeave={() => setOpen(null)}>
                <button type="button" className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary">
                  {s.title} <ChevronDown className={`w-3 h-3 transition-transform ${open === s.title ? 'rotate-180' : ''}`} />
                </button>
                {open === s.title && (
                  <div className="absolute left-0 top-full pt-2 min-w-[200px]">
                    <div className="glass-morphism border border-border rounded-xl shadow-xl py-2">
                      {s.links.map(([name, href]) => (
                        <a key={href} href={href} className="block px-4 py-2 text-sm text-foreground/80 hover:text-primary hover:bg-primary/5">{name}</a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right glob: search, theme, Get Started */}
          <div className={`relative flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 rounded-2xl ${ease} ${split ? `${glass} sbi-shadow-lg translate-y-1` : 'border border-transparent'}`}>
            {/* #10766 (H4): the search opens a box over the live site search (HeaderSearch). */}
            <HeaderSearch />
            <ThemeSwitcher />
            <a href="/get-started" className="inline-flex items-center gap-1.5 px-4 sm:px-5 py-2.5 rounded-xl card-primary-cta font-bold text-sm" {...ctaAttrs(getStartedCtaLabel('home-header'))}>
              <span className="hidden sm:inline">Get Started</span>
              <span className="sm:hidden">Start</span>
              <ArrowRight className="w-4 h-4" aria-hidden />
            </a>
          </div>
        </div>

        {/* The links, from the menu button */}
        {menu && (
          <div className="absolute left-0 top-full mt-3 w-[min(92vw,360px)] glass-morphism border border-border rounded-2xl sbi-shadow-lg p-4 grid grid-cols-2 gap-4">
            {NAV.map((s) => (
              <div key={s.title}>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-foreground/50">{s.title}</p>
                <ul className="mt-2 space-y-1.5">
                  {s.links.map(([name, href]) => (
                    <li key={href}><a href={href} className="text-sm text-foreground/80 hover:text-primary">{name}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}

// --- The page sky: one star backdrop behind the whole page ---------------------------------
// The sky is fixed behind everything, in two layers that drift past at different speeds as you
// scroll (parallax). Sections with a lot of text (the ones this idea keeps from the current
// homepage) become solid rounded cards, so no stars sit behind the text; the sky shows in the
// gaps between them. Light sections (the orbit, the one-line positioning) float on the sky.
// Rendered once, from the Hero. Studio-only page styling: scoped by a class on <html>.

const SKY_CSS = `
html.nf-sky, html.nf-sky body { background: var(--nf-page); }
html.nf-sky #root > div { background: transparent !important; position: relative; z-index: 1; }
html.nf-sky main > section {
  margin: 20px clamp(10px, 2vw, 28px); border-radius: 28px; overflow: hidden; position: relative;
  background: var(--background) !important; border: 1px solid color-mix(in srgb, var(--foreground) 10%, transparent);
  box-shadow: var(--nf-card-shadow);
  transition: transform .7s ease-in-out, opacity .7s ease-in-out;
}
html.nf-sky main > section.nf-out { transform: translateY(28px) scale(.97); opacity: .4; }
/* The doodle cards (light mode): comic-book panels. Light blue, dark text, black ink outline and a
   hard shadow, in every brand theme (some themes, such as midnight-blueprint, are navy in both
   modes, so the colours are set here, not read from the theme). */
html.nf-sky:not(.dark) main > section, html.nf-sky:not(.dark) .nf-panel {
  --background: #DCEAFF; --bg: #DCEAFF; --surface: #FFFFFF; --surface-alt: #F2F7FF;
  --card: #FFFFFF; --card-foreground: #111111; --foreground: #111111; --text: #111111;
  --heading: #111111; --subheading: #111111; --text-muted: #333333;
  --muted: #EEF4FF; --muted-foreground: #333333; --border: #111111; --primary: #1F5FD1;
  /* Tailwind resolves its --color-* once at :root, so set them here too. Accent is the darker
     gold (sunshine-700), readable on white at heading size. */
  --color-background: #DCEAFF; --color-foreground: #111111; --color-card: #FFFFFF;
  --color-card-foreground: #111111; --color-primary: #1F5FD1; --color-primary-foreground: #FFFFFF;
  --color-accent: var(--sunshine-700); --color-border: #111111; --color-muted: #EEF4FF;
  --color-muted-foreground: #333333;
  color: #111111; border: 2.5px solid #111111 !important; box-shadow: 6px 6px 0 #111111;
}
html.nf-sky:not(.dark) :is(main > section, .nf-panel) .rounded-2xl.border,
html.nf-sky:not(.dark) :is(main > section, .nf-panel) .rounded-xl.border { border-width: 2.5px; border-color: #111111; box-shadow: 4px 4px 0 #111111 !important; }
@media (max-width: 639px) { html.nf-sky main > section { border-radius: 20px; margin: 12px 10px; } }
@media (prefers-reduced-motion: reduce) { html.nf-sky main > section, html.nf-sky main > section.nf-out { transition: none; transform: none; opacity: 1; } }
`;

function SkyLayer({ seed, count, size, speed, layerRef }: { seed: number; count: number; size: number; speed: number; layerRef: (el: HTMLDivElement | null) => void }) {
  const stars = useMemo(() => {
    const r = rng(seed);
    return Array.from({ length: count }, () => ({ x: r() * 1440, y: r() * 900, s: (0.5 + r() * 1.1) * size, tw: r() * 6 }));
  }, [seed, count, size]);
  // Two copies, one above the other, so the layer can wrap without a seam.
  return (
    <div className="fixed inset-0 overflow-hidden" aria-hidden>
      <div ref={layerRef} data-speed={speed} className="absolute inset-x-0 top-0 h-[200vh] will-change-transform">
        <svg className="h-full w-full" viewBox="0 0 1440 1800" preserveAspectRatio="xMidYMid slice" style={{ opacity: 'var(--nf-star-alpha)' }}>
          {[0, 900].map((dy) => (
            <g key={dy}>
              {stars.map((s, i) => (
                <circle key={i} cx={s.x} cy={s.y + dy} r={s.s} className="nf-twinkle" style={{ fill: 'var(--nf-star)', animationDelay: `${s.tw}s` }} />
              ))}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

function PageSky() {
  const layers = useRef<HTMLDivElement[]>([]);
  const reduce = useReducedMotionConfig();
  useEffect(() => {
    document.documentElement.classList.add('nf-sky');
    // A stable height for the parallax: a phone's address bar changes innerHeight on every swipe,
    // which made the stars jump. Read it again only when the width changes (a rotation, #10928).
    let width = window.innerWidth;
    let height = window.innerHeight;
    const resize = () => {
      if (window.innerWidth === width) return;
      width = window.innerWidth;
      height = window.innerHeight;
      move();
    };
    const move = () => {
      for (const el of layers.current) {
        if (!el) continue;
        const f = ((window.scrollY * Number(el.dataset.speed)) / height) % 1;
        el.style.transform = `translateY(${-f * 50}%)`;
      }
    };
    if (!reduce) {
      move();
      window.addEventListener('scroll', move, { passive: true });
      window.addEventListener('resize', resize);
    }
    // Each text card settles into place as it comes into view.
    const cards = Array.from(document.querySelectorAll<HTMLElement>('main > section'));
    const io = reduce
      ? null
      : new IntersectionObserver((entries) => {
          for (const e of entries) e.target.classList.toggle('nf-out', !e.isIntersecting && e.boundingClientRect.top > 0);
        }, { rootMargin: '0px 0px -12% 0px' });
    if (io) for (const c of cards) io.observe(c);
    return () => {
      document.documentElement.classList.remove('nf-sky');
      window.removeEventListener('scroll', move);
      window.removeEventListener('resize', resize);
      io?.disconnect();
      for (const c of cards) c.classList.remove('nf-out');
    };
  }, [reduce]);
  return createPortal(
    <div className="pointer-events-none" style={{ position: 'fixed', inset: 0, zIndex: 0, background: 'var(--nf-paper-dots), radial-gradient(120% 90% at 70% 0%, var(--nf-glow) 0%, var(--nf-page) 60%)' }} aria-hidden>
      <style>{NF_THEME}</style>
      <style>{SKY_CSS}</style>
      <SkyLayer seed={11} count={110} size={0.8} speed={0.08} layerRef={(el) => { if (el) layers.current[0] = el; }} />
      <SkyLayer seed={29} count={45} size={1.4} speed={0.22} layerRef={(el) => { if (el) layers.current[1] = el; }} />
    </div>,
    document.body,
  );
}

// --- About: calm, centred, room to breathe ------------------------------------------------
// The current About section is five boxes of text and links. Here, three calm parts on one
// centred axis: the three promises joined by a short contrail; the philosophy as a slow marquee
// with one meaning at a time below it; the two founders. Copy is the current section's
// (HomePage.tsx About). Left out: the three Services one-liners (the Growth orbit says it) and
// the link boxes (the same links are in the header and footer).

const PROMISES = [
  { label: 'Who we are', text: "A team that's been in your shoes. We've run small businesses, navigated chaos, and learned what works." },
  { label: 'What makes us different', text: 'No 40-slide decks. No consultant BS. Real strategies and execution plans you can actually use.' },
  { label: 'Our promise', text: 'Strategy that moves as fast as your business does.' },
];

const PRINCIPLES = [
  { name: 'Strategy Before Tactics', line: 'Build testable roadmaps, not 5-year fiction.' },
  { name: 'Measure Twice, Cut Once', line: 'Launch smart, fix fast, learn faster.' },
  { name: 'Fly Lean & Agile', line: 'Speed beats perfection, momentum beats planning.' },
  { name: 'Build, Measure, Learn, Repeat', line: "Strategy isn't static; neither are we." },
];

const FOUNDERS = [
  { name: 'Ben W, MBA', role: 'Co-Founder & Lead Strategist', img: 'ben', alt: 'Ben Wiltsie', bio: 'Ben is the Co-Founder and Lead Strategist. He works across strategy, operations, and growth, building clear execution plans for small businesses navigating real-world complexity.' },
  { name: 'Winter', role: 'Co-Founder & Chief of Staff', img: 'winter', alt: 'Winter Cormier', bio: 'Winter is the Co-Founder and Chief of Staff. She turns messy ideas into clean systems and visuals, keeping projects and people moving from concept to delivery without anything slipping through the cracks.' },
];

const ABOUT_CSS = `
@keyframes nf-marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
.nf-marquee{animation:nf-marquee 56s linear infinite}
.nf-outline{color:transparent;-webkit-text-stroke:1.5px currentColor}
@media (prefers-reduced-motion:reduce){.nf-marquee{animation:none}}
`;

/** One principle's meaning at a time, fading to the next every few seconds. */
function PrincipleLine() {
  const reduce = useReducedMotionConfig();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const t = window.setInterval(() => setI((n) => (n + 1) % PRINCIPLES.length), 4200);
    return () => window.clearInterval(t);
  }, [reduce]);
  if (reduce) {
    return (
      <ul className="space-y-2 text-foreground/75">
        {PRINCIPLES.map((pr) => (<li key={pr.name}><span className="font-semibold text-foreground">{pr.name}:</span> {pr.line}</li>))}
      </ul>
    );
  }
  const pr = PRINCIPLES[i];
  return (
    <div className="h-16 sm:h-12 relative" aria-live="polite">
      <AnimatePresence mode="wait">
        <motion.p key={pr.name} className="text-lg text-foreground/75" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.45, ease: 'easeInOut' }}>
          <span className="font-semibold text-foreground">{pr.name}:</span> {pr.line}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

export function About() {
  const reduce = useReducedMotionConfig();
  const rise = (i: number) => ({
    initial: reduce ? false : { opacity: 0, y: 18 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.4 },
    transition: { duration: 0.7, delay: i * 0.15, ease: 'easeInOut' as const },
  });
  return (
    <section className="nf-panel relative my-5 mx-[clamp(10px,2vw,28px)] rounded-[20px] sm:rounded-[28px] overflow-hidden border border-foreground/10 bg-background py-24 sm:py-32 text-center">
      <NfTheme />
      <style>{ABOUT_CSS}</style>

      {/* 1. Title and the three promises */}
      <div className="max-w-5xl mx-auto px-6 sm:px-10">
        <h2 className="text-4xl sm:text-5xl font-bold font-heading">
          About <span className="text-primary">SmallBiz Innovation</span>
        </h2>
        <p className="mt-5 text-lg sm:text-xl text-foreground/75 max-w-2xl mx-auto">We give small businesses big-firm strategy and execution, built for how they actually run.</p>

        <div className="relative mt-20">
          <svg className="hidden md:block absolute left-[16%] right-[16%] top-[7px] h-4 w-[68%]" viewBox="0 0 1000 16" preserveAspectRatio="none" aria-hidden>
            <motion.path d="M 0 8 C 300 2, 700 14, 1000 8" fill="none" stroke={BLUE} strokeWidth={2} strokeLinecap="round" vectorEffect="non-scaling-stroke"
              initial={reduce ? false : { pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, amount: 0.6 }} transition={{ duration: 1.6, ease: 'easeInOut' }} />
          </svg>
          <div className="grid md:grid-cols-3 gap-14 md:gap-10">
            {PROMISES.map((pr, i) => (
              <motion.div key={pr.label} className="flex flex-col items-center" {...rise(i)}>
                <span aria-hidden className="nf-dot block h-4 w-4 rounded-full bg-primary" />
                <p className="mt-6 text-sm font-semibold uppercase tracking-[0.16em] text-primary">{pr.label}</p>
                <p className="mt-3 text-xl sm:text-2xl leading-snug font-bold font-heading max-w-xs">{pr.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* 2. The philosophy: a slow marquee, one meaning at a time */}
      <div className="mt-32">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">Our philosophy · Candid. Warm. Wry.</p>
        <div className="mt-8 overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]" aria-hidden>
          <div className="nf-marquee flex w-max whitespace-nowrap text-4xl sm:text-6xl leading-[1.3] py-3 font-bold font-heading">
            {[0, 1].map((copy) => (
              <div key={copy} className="flex">
                {PRINCIPLES.map((pr, i) => (
                  <span key={pr.name} className={`px-8 ${i % 2 ? 'nf-outline text-foreground/60' : 'text-foreground'}`}>
                    {pr.name}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-12 max-w-2xl mx-auto px-6">
          <PrincipleLine />
          <a href="/about" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">Read more <ArrowRight className="w-4 h-4" aria-hidden /></a>
        </div>
      </div>

      {/* 3. The founders */}
      <div className="mt-32 max-w-5xl mx-auto px-6 sm:px-10">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">The founders</p>
        <div className="mt-10 grid md:grid-cols-2 gap-16 md:gap-12">
          {FOUNDERS.map((f, i) => (
            <motion.div key={f.name} className="group flex flex-col items-center" {...rise(i)}>
              {/* An airplane window: a tall rounded pane in a thick frame, with a faint glare on the glass. */}
              <div
                className="nf-window relative shrink-0 p-2.5 transition-transform duration-500 ease-in-out group-hover:-translate-y-1"
                style={{ borderRadius: '48% / 38%' }}
              >
                <div className="nf-window-pane relative overflow-hidden w-28 sm:w-32 aspect-[4/5]" style={{ borderRadius: '48% / 38%', boxShadow: 'inset 0 0 0 2px rgba(74,137,247,.45), inset 0 6px 14px rgba(0,0,0,.45)' }}>
                  <img
                    src={`https://images.smallbizinnovation.com/m/team/${f.img}.png?width=288`}
                    alt={f.alt}
                    width={128}
                    height={160}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <span aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,.18) 0%, rgba(255,255,255,0) 38%)', borderRadius: 'inherit' }} />
                </div>
              </div>
              <p className="mt-6 text-2xl font-bold font-heading">{f.name}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] nf-gold">{f.role}</p>
              <p className="mt-4 text-foreground/75 max-w-sm">{f.bio}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-14 flex flex-wrap justify-center gap-3">
          <a href="/about" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold border border-foreground/20 hover:border-primary hover:text-primary transition-colors">Our Story <ArrowRight className="w-4 h-4" aria-hidden /></a>
          <a href="/team" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold card-primary-cta">Meet the Team <ArrowRight className="w-4 h-4" aria-hidden /></a>
        </div>
      </div>
    </section>
  );
}

// --- Reasons: the current section, one line changed ------------------------------------------
// The same three cards as the current homepage (HomePage.tsx, Three Core Differentiators), on
// the same solid card as the other text sections. Only change: the third card's last line said
// "We deliver greater value than freelancers or DIY approaches by taking full ownership of
// results." We work with freelancers, so it now keeps the claim without the comparison
// (Winter's call; Ben may prefer a comparison when the page has to make the case).

export function Reasons() {
  const cards = [
    {
      icon: <Target className="w-5 h-5 text-primary" />, tint: 'bg-primary/10', title: 'Full-Stack: Strategy AND Execution',
      lines: ["We're your full-stack strategy & execution partner, not just a consultancy.", 'We build the strategy and we stay in to help run it.', 'Not a consultant who hands you a slide deck.'],
      last: 'A partner who stays in it with you.',
    },
    {
      icon: <TrendingUp className="w-5 h-5 text-accent" />, tint: 'bg-accent/10', title: 'Across All Business Functions',
      lines: ["We don't specialize in just one slice of your business.", 'Our scope spans marketing, operations, finance, systems, and strategic planning.', "Because real growth isn't isolated."],
      last: 'Every function affects the others and we align them all.',
    },
    {
      icon: <Users className="w-5 h-5 text-primary" />, tint: 'bg-primary/10', title: 'Built for Small Business',
      lines: ['We give small businesses big-firm strategy and execution, built for how they actually run.', 'Big-firm thinking. Small-business reality.'],
      last: 'We take full ownership of results.',
    },
  ];
  return (
    <section className="nf-panel relative my-5 mx-[clamp(10px,2vw,28px)] rounded-[20px] sm:rounded-[28px] overflow-hidden border border-foreground/10 bg-background py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-primary tracking-wide mb-3">What sets us apart</p>
          <h2 className="text-3xl sm:text-4xl font-bold">Three Reasons We Are Different</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {cards.map((c) => (
            <div key={c.title} className="rounded-2xl p-8 bg-card/80 border border-border sbi-shadow-md flex flex-col gap-4">
              <div className={`w-10 h-10 rounded-xl ${c.tint} flex items-center justify-center`}>{c.icon}</div>
              <h3 className="text-xl font-bold">{c.title}</h3>
              <div className="text-foreground/80 space-y-2 text-sm leading-relaxed">
                {c.lines.map((l) => <p key={l}>{l}</p>)}
                <p className="font-semibold text-foreground">{c.last}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
